import { useState, useEffect } from 'react'
import {
  Box, Card, CardContent, Typography, Button, TextField, Grid,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, Chip, IconButton, Dialog, DialogTitle, DialogContent,
  DialogActions, FormControl, InputLabel, Select, MenuItem,
  CircularProgress, Alert, Snackbar, Tooltip,
} from '@mui/material'
import { Add, Edit, Delete } from '@mui/icons-material'
import { analyticsApi } from '../services/api'

const DRIFT_COMPONENTS = [
  { value: 'goal_text_preview', label: 'Goal / System Prompt' },
  { value: 'goal_embedding_hash', label: 'Goal Embedding' },
  { value: 'tool_permission_signature', label: 'Tool Permissions' },
  { value: 'memory_access_signature', label: 'Memory Access' },
  { value: 'orchestration_dependency_signature', label: 'Orchestration' },
  { value: 'error_posture_score', label: 'Error Posture' },
]

const PRIORITIES = ['high', 'medium', 'low']

const CATEGORIES = [
  'prompt_engineering', 'agent_design', 'memory_architecture',
  'orchestration_soundness', 'observability_tracing', 'governance_alignment',
]

const emptyForm = {
  drift_component: 'goal_text_preview',
  test: '',
  priority: 'medium',
  category: 'agent_design',
  sort_order: 0,
}

const ACIATemplatesPanel = () => {
  const [templates, setTemplates] = useState<any[]>([])
  const [loading, setLoading] = useState(true)
  const [dialogOpen, setDialogOpen] = useState(false)
  const [editingTemplate, setEditingTemplate] = useState<any>(null)
  const [form, setForm] = useState(emptyForm)
  const [saving, setSaving] = useState(false)
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({ open: false, message: '', severity: 'success' })

  useEffect(() => { fetchTemplates() }, [])

  const fetchTemplates = async () => {
    setLoading(true)
    try {
      const res = await analyticsApi.getACIATemplates({ active_only: false })
      setTemplates(res.data || [])
    } catch (e) {
      console.error('Failed to fetch ACIA templates:', e)
    } finally {
      setLoading(false)
    }
  }

  const handleSave = async () => {
    if (!form.test) return
    setSaving(true)
    try {
      if (editingTemplate) {
        await analyticsApi.updateACIATemplate(editingTemplate.id, form)
        setSnackbar({ open: true, message: 'Template updated', severity: 'success' })
      } else {
        await analyticsApi.createACIATemplate(form)
        setSnackbar({ open: true, message: 'Template created', severity: 'success' })
      }
      setDialogOpen(false)
      setEditingTemplate(null)
      setForm(emptyForm)
      fetchTemplates()
    } catch (e: any) {
      setSnackbar({ open: true, message: e.response?.data?.detail || 'Failed to save', severity: 'error' })
    } finally {
      setSaving(false)
    }
  }

  const handleEdit = (t: any) => {
    setForm({
      drift_component: t.drift_component,
      test: t.test,
      priority: t.priority,
      category: t.category,
      sort_order: t.sort_order || 0,
    })
    setEditingTemplate(t)
    setDialogOpen(true)
  }

  const handleDelete = async (id: string) => {
    if (!confirm('Deactivate this template?')) return
    try {
      await analyticsApi.deleteACIATemplate(id)
      setSnackbar({ open: true, message: 'Template deactivated', severity: 'success' })
      fetchTemplates()
    } catch {
      setSnackbar({ open: true, message: 'Failed to deactivate', severity: 'error' })
    }
  }

  const componentLabel = (value: string) =>
    DRIFT_COMPONENTS.find(d => d.value === value)?.label || value.replace(/_/g, ' ')

  // Group by drift component
  const grouped: Record<string, any[]> = {}
  templates.forEach(t => {
    if (!grouped[t.drift_component]) grouped[t.drift_component] = []
    grouped[t.drift_component].push(t)
  })

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Box>
          <Typography variant="h6">Change Impact Analysis Templates</Typography>
          <Typography variant="body2" color="text.secondary">
            Test recommendations shown to Enterprise customers when agent behavior drifts
          </Typography>
        </Box>
        <Button variant="contained" startIcon={<Add />} onClick={() => { setForm(emptyForm); setEditingTemplate(null); setDialogOpen(true) }}>
          New Template
        </Button>
      </Box>

      {Object.entries(grouped).map(([component, temps]) => (
        <Card key={component} sx={{ mb: 2 }}>
          <CardContent>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
              {componentLabel(component)}
            </Typography>
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Test Recommendation</TableCell>
                    <TableCell width={80}>Priority</TableCell>
                    <TableCell width={150}>Dimension</TableCell>
                    <TableCell width={60}>Order</TableCell>
                    <TableCell width={60}>Status</TableCell>
                    <TableCell width={80} align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {temps.sort((a: any, b: any) => a.sort_order - b.sort_order).map((t: any) => (
                    <TableRow key={t.id} hover sx={{ opacity: t.active ? 1 : 0.5 }}>
                      <TableCell>
                        <Typography variant="body2">{t.test}</Typography>
                      </TableCell>
                      <TableCell>
                        <Chip label={t.priority} size="small"
                          color={t.priority === 'high' ? 'error' : t.priority === 'medium' ? 'warning' : 'default'} />
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption" sx={{ textTransform: 'capitalize' }}>
                          {t.category?.replace(/_/g, ' ')}
                        </Typography>
                      </TableCell>
                      <TableCell>{t.sort_order}</TableCell>
                      <TableCell>
                        <Chip label={t.active ? 'Active' : 'Inactive'} size="small"
                          color={t.active ? 'success' : 'default'} variant="outlined" />
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip title="Edit">
                          <IconButton size="small" onClick={() => handleEdit(t)}><Edit fontSize="small" /></IconButton>
                        </Tooltip>
                        {t.active && (
                          <Tooltip title="Deactivate">
                            <IconButton size="small" onClick={() => handleDelete(t.id)}><Delete fontSize="small" /></IconButton>
                          </Tooltip>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      ))}

      {templates.length === 0 && (
        <Alert severity="info">No ACIA templates found. Templates will be seeded automatically on next analytics service restart.</Alert>
      )}

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>{editingTemplate ? 'Edit Template' : 'Create Template'}</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <FormControl fullWidth>
                <InputLabel>Drift Component</InputLabel>
                <Select value={form.drift_component} label="Drift Component"
                  onChange={e => setForm({ ...form, drift_component: e.target.value })}>
                  {DRIFT_COMPONENTS.map(d => <MenuItem key={d.value} value={d.value}>{d.label}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={3}>
              <FormControl fullWidth>
                <InputLabel>Priority</InputLabel>
                <Select value={form.priority} label="Priority"
                  onChange={e => setForm({ ...form, priority: e.target.value })}>
                  {PRIORITIES.map(p => <MenuItem key={p} value={p}>{p}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={3}>
              <TextField fullWidth label="Sort Order" type="number" value={form.sort_order}
                onChange={e => setForm({ ...form, sort_order: parseInt(e.target.value) || 0 })} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth label="Test Recommendation" multiline rows={3} required
                placeholder="Describe the test that should be run when this component drifts..."
                value={form.test} onChange={e => setForm({ ...form, test: e.target.value })} />
            </Grid>
            <Grid item xs={6}>
              <FormControl fullWidth>
                <InputLabel>Dimension</InputLabel>
                <Select value={form.category} label="Dimension"
                  onChange={e => setForm({ ...form, category: e.target.value })}>
                  {CATEGORIES.map(c => <MenuItem key={c} value={c}>{c.replace(/_/g, ' ')}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={saving || !form.test}>
            {saving ? 'Saving...' : editingTemplate ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snackbar.open} autoHideDuration={3000} onClose={() => setSnackbar({ ...snackbar, open: false })}>
        <Alert severity={snackbar.severity}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  )
}

export default ACIATemplatesPanel
